import React, { useCallback, useEffect, useState } from 'react';
import {
  ShieldAlert, Loader2, RefreshCw, CheckCircle2, XCircle, ExternalLink, Info,
} from 'lucide-react';
import { toast } from 'sonner';
import { DataTable } from '../security/SecurityShared';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const REASONS = {
  new_client_single_limit: 'New client — above the per-top-up limit',
  single_recharge_over_limit: 'Above the single top-up ceiling',
  balance_cap: 'Would exceed the balance cap',
  no_payment_id: 'No provider payment id',
};

const STATUS_STYLES = {
  pending: 'bg-amber-100 text-amber-700',
  approved: 'bg-emerald-100 text-emerald-700',
  rejected: 'bg-rose-100 text-rose-700',
  approving: 'bg-slate-100 text-slate-600',
};

/**
 * Queue of real, provider-confirmed payments that breached a ceiling and
 * were therefore NOT credited. Approve → money hits the balance with a
 * ledger row. Reject → nothing moves (refund stays manual in Stripe/PayPal).
 */
export default function RechargeReviewsPanel({ token, onDecided }) {
  const [status, setStatus] = useState('pending');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState('');
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const r = await fetch(`${API_URL}/api/admin/risk/recharge-reviews?status=${status}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || `HTTP ${r.status}`);
      setRows(j.rows || []);
    } catch (e) {
      setErr(e.message || 'Network error');
    } finally { setLoading(false); }
  }, [token, status]);

  useEffect(() => { load(); }, [load]);

  const decide = async (row, action) => {
    let body = {};
    if (action === 'reject') {
      const reason = window.prompt(
        `Reject €${Number(row.held || 0).toFixed(2)} for ${row.user_email}?\n\n`
        + 'No money will be credited. If the payment is genuine, refund it manually '
        + 'in the Stripe / PayPal dashboard.\n\nReason:');
      if (!reason || !reason.trim()) return;
      body = { reason: reason.trim() };
    } else if (!window.confirm(
      `Credit €${Number(row.held || 0).toFixed(2)} to ${row.user_email}?\n\n`
      + `Payment: ${row.method || '—'} ${row.external_txn_id || ''}`)) {
      return;
    }
    setBusyId(row.id);
    try {
      const r = await fetch(`${API_URL}/api/admin/risk/recharge-reviews/${row.id}/${action}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || `HTTP ${r.status}`);
      toast.success(action === 'approve'
        ? `Credited €${Number(j.credited || 0).toFixed(2)} — new balance €${Number(j.balance_after || 0).toFixed(2)}`
        : 'Marked as rejected — no money was credited');
      await load();
      if (onDecided) onDecided();
    } catch (e) {
      toast.error(e.message || 'Failed');
    } finally { setBusyId(''); }
  };

  return (
    <div className="w-full" data-testid="recharge-reviews-panel">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex items-center gap-2">
          <ShieldAlert className="w-5 h-5 text-amber-600" />
          <h2 className="text-lg md:text-xl font-bold text-slate-900">Recharges on review</h2>
        </div>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          data-testid="recharge-reviews-status"
          className="px-3 py-1.5 text-sm font-semibold rounded-lg bg-white border border-slate-300"
        >
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="all">All</option>
        </select>
        <button
          onClick={load}
          data-testid="recharge-reviews-refresh"
          className="ml-auto px-3 py-1.5 bg-slate-800 hover:bg-slate-900 text-white rounded-lg text-sm font-medium flex items-center gap-1.5"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      <div className="flex items-start gap-2 p-3 mb-4 rounded-lg bg-sky-50 border border-sky-200 text-[13px] text-sky-900">
        <Info className="w-4 h-4 mt-0.5 shrink-0" />
        <p>
          These payments really arrived at Stripe / PayPal but exceeded the configured
          ceilings, so the balance was not credited. <strong>Approve</strong> credits the held
          amount and records who approved it. <strong>Reject</strong> changes nothing —
          refund a genuine payment manually in the provider dashboard.
          Ceilings live in <strong>Settings → Limits &amp; anti-abuse</strong>.
        </p>
      </div>

      {err && <div className="p-3 mb-3 rounded-lg bg-rose-50 border border-rose-200 text-sm text-rose-700" data-testid="recharge-reviews-error">{err}</div>}

      {loading && rows.length === 0 ? (
        <div className="py-12 flex justify-center" data-testid="recharge-reviews-loading"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
      ) : rows.length === 0 ? (
        <div className="py-10 text-center text-sm text-slate-500 bg-white rounded-xl border border-slate-200" data-testid="recharge-reviews-empty">
          Nothing here — no recharges are waiting for a decision.
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 p-2 md:p-0 md:overflow-x-auto">
          <DataTable testid="recharge-reviews-table" rows={rows}
            rowKey={(row) => row.id} rowTestid={(row) => `recharge-review-row-${row.id}`}
            cols={[
              { key: 'cust', label: 'Customer', primary: true,
                render: (row) => (
                  <>
                    <div className="font-semibold text-slate-800 break-all">{row.user_email || '—'}</div>
                    {row.user_account_id && <div className="text-[11px] text-slate-400">#{row.user_account_id}</div>}
                  </>) },
              { key: 'held', label: 'Held', right: true,
                tdClass: 'tabular-nums font-bold text-amber-700',
                render: (row) => <span className="tabular-nums font-bold text-amber-700">€{Number(row.held || 0).toFixed(2)}</span> },
              { key: 'date', label: 'Date', tdClass: 'whitespace-nowrap text-slate-600',
                render: (row) => (row.created_at ? new Date(row.created_at).toLocaleString() : '—') },
              { key: 'paid', label: 'Paid', right: true, tdClass: 'text-right tabular-nums font-semibold',
                render: (row) => `€${Number(row.amount || 0).toFixed(2)}` },
              { key: 'credited', label: 'Credited', right: true, tdClass: 'text-right tabular-nums text-emerald-700',
                render: (row) => `€${Number(row.credited || 0).toFixed(2)}` },
              { key: 'reason', label: 'Reason', tdClass: 'text-slate-600',
                render: (row) => REASONS[row.reason] || row.reason || '—' },
              { key: 'method', label: 'Method / payment id', full: true,
                render: (row) => (
                  <>
                    <div className="uppercase text-[11px] font-bold text-slate-500">{row.method || '—'}</div>
                    <div className="font-mono text-[11px] text-slate-600 break-all flex items-center gap-1">
                      {row.external_txn_id || '—'}
                      {row.method === 'stripe' && row.external_txn_id && (
                        <a
                          href={`https://dashboard.stripe.com/search?query=${encodeURIComponent(row.external_txn_id)}`}
                          target="_blank" rel="noreferrer"
                          className="text-sky-600 hover:text-sky-800"
                          data-testid={`recharge-review-stripe-link-${row.id}`}
                        ><ExternalLink className="w-3 h-3" /></a>
                      )}
                    </div>
                  </>) },
              { key: 'status', label: 'Status',
                render: (row) => (
                  <>
                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${STATUS_STYLES[row.status] || 'bg-slate-100 text-slate-600'}`}>
                      {row.status}
                    </span>
                    {row.decided_by && <div className="text-[10px] text-slate-400 mt-0.5">{row.decided_by}</div>}
                    {row.reject_reason && <div className="text-[10px] text-rose-500 mt-0.5">{row.reject_reason}</div>}
                  </>) },
              { key: 'action', label: 'Action', right: true, full: true,
                render: (row) => (row.status === 'pending' ? (
                  <div className="inline-flex gap-1.5">
                    <button
                      onClick={() => decide(row, 'approve')}
                      disabled={busyId === row.id}
                      data-testid={`recharge-review-approve-${row.id}`}
                      className="px-2.5 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-bold flex items-center gap-1 disabled:opacity-50"
                    >
                      {busyId === row.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />} Approve
                    </button>
                    <button
                      onClick={() => decide(row, 'reject')}
                      disabled={busyId === row.id}
                      data-testid={`recharge-review-reject-${row.id}`}
                      className="px-2.5 py-1.5 bg-white border border-rose-300 text-rose-700 hover:bg-rose-50 rounded-lg text-xs font-bold flex items-center gap-1 disabled:opacity-50"
                    >
                      <XCircle className="w-3.5 h-3.5" /> Reject
                    </button>
                  </div>
                ) : (
                  <span className="text-[11px] text-slate-400">
                    {row.decided_at ? new Date(row.decided_at).toLocaleString() : '—'}
                  </span>
                )) },
            ]} />
        </div>
      )}
    </div>
  );
}
